'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import Headband from '@/app/components/ui/Headband/Headband';
import DesktopNav from '@/app/components/ui/Header/Navs/DesktopNav';
import MobileNav from '@/app/components/ui/Header/Navs/MobileNav';

export default function Navbar({ content, searchIndex }: any) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Hide header immediately on scroll
      setIsHeaderVisible(false);

      // Clear previous timeout for showing header
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }

      // Set a new timeout to show header when scrolling stops
      visibilityTimeoutRef.current = setTimeout(() => {
        setIsHeaderVisible(true);
      }, 250); // Adjust timeout as needed (e.g., 250ms)

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (currentScrollY >= 25) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        if (window.scrollY < 25) {
          setHasScrolled(false);
        }
        // If window.scrollY >= 25, hasScrolled is already true from above.
      }, 150); // Adjust timeout duration as needed

      // Update lastScrollY for reference, though not directly driving visibility anymore
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (visibilityTimeoutRef.current) {
        clearTimeout(visibilityTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 z-50 w-full transition-transform duration-300 ease-in-out ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <Link
        aria-label="Skip to main content"
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-BLACK focus:px-4 focus:py-2 focus:text-WHITE focus:outline-none focus:ring-2 focus:ring-WHITE/40 [@media(pointer:coarse)]:hidden"
      >
        Skip Navigation
      </Link>
      {content.headband && content.showHeadband && <Headband content={content.headband} />}

      <DesktopNav content={content} searchIndex={searchIndex} hasScrolled={hasScrolled} />
      <MobileNav
        content={content}
        isOpen={mobileMenuOpen}
        setIsOpen={setMobileMenuOpen}
        searchIndex={searchIndex}
        hasScrolled={hasScrolled}
      />
    </nav>
  );
}
